import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { useSelector } from "react-redux";
import { Chart as ChartJS, Filler } from "chart.js";
import dayjs from "dayjs";
import "./MCChart.less";
import { datasetsOptions, getChartOptions } from "./chartOptions";
import MCChartTooltip from "./MCChartTooltip";
import { tooltipLineWithPoints } from "../../../../DashboardPage/components/chartsPlagins";
import ChartLegend from "../../../components/ChartLegend/ChartLegend";

ChartJS.register(Filler);

const PREV_PREFIX = {
  "previous month": "Prev. month",
  "previous year": "Prev. year",
  "previous week": "Prev. week",
  "previous period": "Prev. period",
};

const getPrevPrefix = (label) => {
  if (!label) return "Prev.";
  return PREV_PREFIX[label.trim().toLowerCase()] || "Prev.";
};

const SERIES_KEYS = {
  rate: {
    chargeback: "chargeback_rate",
    fraud: "fraud_rate",
    three_ds: "three_ds_usage_rate",
  },
  count: {
    chargeback: "chargeback_count",
    fraud: "fraud_count",
    three_ds: "three_ds_payment_count",
  },
};

const SERIES_LABELS = {
  rate: {
    chargeback: "Chargeback Rate",
    fraud: "Fraud Rate",
    three_ds: "3DS Usage",
  },
  count: {
    chargeback: "Chargeback Count",
    fraud: "Fraud Count",
    three_ds: "3DS Payment Count",
  },
};

const MCChart = ({ data, dataType }) => {
  const { theme } = useSelector((state) => state.nav);
  const [activeSeries, setActiveSeries] = useState(() => new Set());
  const isSeriesVisible = (id) =>
    activeSeries.size === 0 || activeSeries.has(id);
  const toggleSeries = (id) =>
    setActiveSeries((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const prevPrefix = getPrevPrefix(data?.comparison_period?.label);

  const tooltipRef = useRef();
  const setTooltipImperative = useCallback((p) => {
    tooltipRef.current?.update(p);
  }, []);

  const chartData = useMemo(() => {
    if (!data) return null;

    const mainDict = data?.data ?? {};
    const compDict = data?.comparison?.data ?? {};
    const mainLabels = Object.keys(mainDict).sort();
    const compLabels = Object.keys(compDict).sort();

    if (mainLabels.length === 0) return null;

    const mainSet = new Set(mainLabels);
    const hasOverlap =
      compLabels.length > 0 && compLabels.some((l) => mainSet.has(l));

    let labels;
    if (hasOverlap) {
      labels = Array.from(new Set([...mainLabels, ...compLabels])).sort();
    } else {
      labels = compLabels.length > mainLabels.length ? compLabels : mainLabels;
    }

    const mainStart = mainLabels[0];
    const compStart = compLabels[0];
    const deltaDays =
      mainStart && compStart
        ? dayjs(mainStart).diff(dayjs(compStart), "day")
        : null;

    const shadowCompDate = (label) => {
      if (deltaDays == null || !label) return null;
      return dayjs(label).subtract(deltaDays, "day").format("YYYY-MM-DD");
    };
    const shadowMainDate = (label) => {
      if (deltaDays == null || !label) return null;
      return dayjs(label).add(deltaDays, "day").format("YYYY-MM-DD");
    };

    const compLabelsAligned = hasOverlap
      ? labels.map((l) => (compDict[l] != null ? l : shadowCompDate(l)))
      : labels.map((_, i) => compLabels[i] ?? shadowCompDate(labels[i]));

    const mainLabelsAligned = hasOverlap
      ? labels.map((l) => (mainDict[l] != null ? l : shadowMainDate(l)))
      : labels.map((_, i) => mainLabels[i] ?? shadowMainDate(labels[i]));

    const pickAt = (dict, srcLabels, targetLabels, key) => {
      if (hasOverlap) {
        return targetLabels.map((l) =>
          dict[l] != null ? dict[l]?.[key] : null
        );
      }
      return targetLabels.map((_, i) => {
        const l = srcLabels[i];
        return l != null && dict[l] != null ? dict[l]?.[key] : null;
      });
    };

    const pointDatasetOptions =
      labels.length === 1
        ? { pointHoverRadius: 6, pointRadius: 4, tension: 0, borderJoinStyle: "round" }
        : { pointRadius: 0, pointHoverRadius: 0, tension: 0, borderJoinStyle: "round" };

    const comparisonOverlayOptions = {
      borderDash: [8, 6],
      borderWidth: 1.5,
      pointRadius: 0,
      pointHoverRadius: 0,
    };

    const datasets = [];
    const pushSeries = (opts, label, key) => {
      datasets.push({
        ...opts,
        ...pointDatasetOptions,
        label,
        fill: opts.fill ?? "origin",
        spanGaps: false,
        hidden: !isSeriesVisible(opts.datasetType),
        data: pickAt(mainDict, mainLabels, labels, key),
        mainLabels: mainLabelsAligned,
      });
      if (compLabels.length) {
        const compType = `${opts.datasetType}_previous`;
        datasets.push({
          ...opts,
          ...comparisonOverlayOptions,
          label: `${prevPrefix} ${label}`,
          datasetType: compType,
          fill: false,
          spanGaps: false,
          hidden: !isSeriesVisible(compType),
          data: pickAt(compDict, compLabels, labels, key),
          comparisonLabels: compLabelsAligned,
        });
      }
    };

    const pushThreshold = (opts, label, value) => {
      datasets.push({
        ...opts,
        ...pointDatasetOptions,
        label,
        fill: false,
        hidden: !isSeriesVisible(opts.datasetType),
        data: labels.map(() => value),
      });
    };

    const keys = SERIES_KEYS[dataType];
    const seriesLabels = SERIES_LABELS[dataType];
    const thresholds = data.thresholds ?? {};

    pushSeries(datasetsOptions.chargeback, seriesLabels.chargeback, keys.chargeback);
    pushSeries(datasetsOptions.fraud, seriesLabels.fraud, keys.fraud);
    pushSeries(datasetsOptions.three_ds, seriesLabels.three_ds, keys.three_ds);
    pushThreshold(
      datasetsOptions.ecm_zone,
      "ECM Zone",
      dataType === "rate" ? thresholds.ecm?.rate : thresholds.ecm?.count
    );
    pushThreshold(
      datasetsOptions.hecm_zone,
      "HECM Zone",
      dataType === "rate" ? thresholds.hecm?.rate : thresholds.hecm?.count
    );

    if (dataType === "rate" && thresholds.efm?.rate != null) {
      pushThreshold(datasetsOptions.efm_zone, "EFM Zone", thresholds.efm.rate);
    }

    return { labels, datasets };
  }, [data, dataType, activeSeries, prevPrefix]);

  const visibleSeries = useMemo(
    () =>
      new Set(
        (chartData?.datasets ?? [])
          .filter((ds) => ds.datasetType && !ds.hidden)
          .map((ds) => ds.datasetType)
      ),
    [chartData]
  );

  const legendItems = useMemo(
    () =>
      (chartData?.datasets ?? [])
        .filter((ds) => ds.datasetType)
        .map((ds) => ({
          id: ds.datasetType,
          color: ds.borderColor,
          dashed: Array.isArray(ds.borderDash) && ds.borderDash.length > 0,
          label: ds.label,
        })),
    [chartData]
  );

  const legendIdsKey = legendItems.map((i) => i.id).join("|");
  useEffect(() => {
    if (!legendIdsKey) return;
    setActiveSeries(new Set(legendIdsKey.split("|")));
  }, [legendIdsKey]);

  const chartOptions = useMemo(
    () => getChartOptions(theme, setTooltipImperative, dataType, visibleSeries),
    [theme, dataType, visibleSeries]
  );

  const isEveryLineHidden = Boolean(chartData?.datasets?.length) && visibleSeries.size === 0;

  return (
    <div className="health-monitor-main-chart">
      <MCChartTooltip ref={tooltipRef} dataType={dataType} />
      <div style={{ paddingBottom: 22 }}>
        {isEveryLineHidden ? (
          <div className="health-monitor-main-chart__empty">
            No lines selected. Turn on a line below to see the chart.
          </div>
        ) : (
          <Line
            style={{ height: 300 }}
            data={chartData ?? { labels: [], datasets: [] }}
            options={chartOptions}
            plugins={[tooltipLineWithPoints]}
          />
        )}
      </div>
      {legendItems.length > 0 && (
        <ChartLegend
          items={legendItems}
          isActive={(id) => activeSeries.has(id)}
          onToggle={toggleSeries}
          style={{ marginBottom: 12 }}
        />
      )}
    </div>
  );
};

export default MCChart;
